<template>
  <div>
    <template v-if="loadingSequence">
      <LoadingComponent :message="$t('general.loadingSequenceStatistics')" />
    </template>

    <template v-else-if="!loadingSequence && !wereSequenceStatisticsFound">
      <NotFound height="75vh"
                :message="$t('general.sequenceStatisticsNotFound')" />
    </template>

    <template v-else>
      <v-row>
        <v-col cols="12" class="d-flex align-center justify-space-between py-1">
          <div class="pl-2">
            <h3 class="kbDarkBlueText--text text-uppercase">
              {{ selectedSequence.name }}
            </h3>
            <h4 class="kbSemiLightGrayText--text text-capitalize font-weight-regular mt-2">
              {{ $t('general.sequenceStatistics') }}
            </h4>
          </div>
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn color="mainButtons"
                     icon
                     outlined
                     v-on="on"
                     @click="goToSequence()">
                <v-icon>mdi-arrow-left</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('general.goBackToTheSequence') }}</span>
          </v-tooltip>
        </v-col>

        <v-col cols="12">
          <v-divider />
        </v-col>

        <v-col cols="12">
          <span class="kbDarkBlueText--text text-h5 pl-2">
            {{ $t('general.emailStatistics') }}
          </span>
          <div class="card-chart kbWhite mt-3">
            <LineChart style="height: 380px"
                       :chart-data="emailData"
                       :options="chartOptions"
                       ref="lineChart1"/>
          </div>
        </v-col>

        <v-col cols="12">
          <span class="kbDarkBlueText--text text-h5 pl-2">
            {{ $t('general.taskStatistics') }}
          </span>
          <div class="card-chart kbWhite mt-3">
            <LineChart style="height: 300px"
                       :chart-data="taskData"
                       :options="chartOptions"
                       ref="lineChart3"/>
          </div>
        </v-col>

        <v-col cols="12">
          <span class="kbDarkBlueText--text text-h5 pl-2">
            {{ $t('general.smsStatistics') }}
          </span>
          <div class="card-chart kbWhite mt-3">
            <BarChart style="height: 300px"
                      :chart-data="smsData"
                      :options="chartOptions"
                      ref="lineChart2"/>
          </div>
        </v-col>
      </v-row>
    </template>
  </div>
</template>

<script>
import BarChart from '@/components/chart/BarChart'
import LineChart from '@/components/chart/LineChart'
import { mapState } from 'vuex'
import notifications from '@/libs/notifications'
import LoadingComponent from '@/components/ui/LoadingComponent'
import NotFound from '@/components/ui/NotFound'
export default {
  components: { NotFound, LoadingComponent, BarChart, LineChart },
  computed: {
    ...mapState({
      selectedSequence: state => state.sequences.selectedSequence
    }),
    statisticsInformation () {
      let statisticsInformation = {
        email: null,
        sms: null,
        task: null
      }
      if (!this.wereSequenceStatisticsFound) {
        return statisticsInformation
      } else {
        statisticsInformation = {
          email: {
            labels: [],
            values: []
          },
          sms: {
            labels: [],
            values: []
          },
          task: {
            labels: [],
            values: []
          }
        }
        const { email, sms, task } = this.selectedSequence.info
        statisticsInformation.email.labels = Object.keys(email)
        statisticsInformation.email.values = Object.values(email)
        statisticsInformation.sms.labels = Object.keys(sms)
        statisticsInformation.sms.values = Object.values(sms)
        statisticsInformation.task.labels = Object.keys(task)
        statisticsInformation.task.values = Object.values(task)
        return statisticsInformation
      }
    },
    emailData () {
      const datasets = [{
        /* backgroundColor: 'rgba(45, 206, 152, 0.3)',
        borderColor: '#2DCE98', */
        backgroundColor: 'rgba(251,144,51,0.6)',
        borderColor: 'rgb(251, 99, 64)',
        label: this.$t('general.emailHooksInformation'),
        data: this.statisticsInformation.email.values,
        showLine: true
      }]
      const labels = this.statisticsInformation.email.labels
      return { datasets, labels }
    },
    smsData () {
      const datasets = [{
        backgroundColor: 'rgba(45, 206, 152, 0.3)',
        borderColor: '#2DCE98',
        label: this.$t('general.smsHooksInformation'),
        data: this.statisticsInformation.sms.values,
        showLine: true
      }]
      const labels = this.statisticsInformation.sms.labels
      return { datasets, labels }
    },
    taskData () {
      const datasets = [{
        backgroundColor: 'rgba(246, 98, 119, 0.3)',
        borderColor: '#F66277',
        label: this.$t('general.taskHooksInformation'),
        data: this.statisticsInformation.task.values,
        showLine: true
      }]
      const labels = this.statisticsInformation.task.labels
      return { datasets, labels }
    },
    chartOptions () {
      let options = {
        responsive: true,
        maintainAspectRatio: false
      }
      const darkThemeOptions = {
        legend: {
          labels: {
            fontColor: '#c4c4c4'
          }
        },
        scales: {
          yAxes: [{
            gridLines: {
              color: '#14192A'
            },
            ticks: {
              fontColor: '#c4c4c4'
            }
          }],
          xAxes: [{
            gridLines: {
              color: '#14192A'
            },
            ticks: {
              fontColor: '#c4c4c4'
            }
          }]
        }
      }
      if (this.$vuetify.theme.isDark) {
        options = { ...options, ...darkThemeOptions }
      }
      return options
    },
    wereSequenceStatisticsFound () {
      return !(!this.selectedSequence ||
        !Object.keys(this.selectedSequence).length ||
        !this.selectedSequence.info)
    }
  },
  created () {
    if (!this.selectedSequence ||
        !Object.keys(this.selectedSequence).length ||
        this.$route.params.sequenceId !== this.selectedSequence.id) {
      this.getSequence()
    }
  },
  data () {
    return {
      loadingSequence: false
    }
  },
  methods: {
    async getSequence () {
      try {
        this.loadingSequence = true
        await Promise.all([
          this.getSequencePeople(),
          this.getSequenceService(),
          this.getSequenceStepsService()
        ])
        this.loadingSequence = false
      } catch (e) {
        this.loadingSequence = false
        notifications.error(e)
      }
    },
    async getSequencePeople () {
      await this.$store.dispatch('getSequencePeople', {
        appId: this.$store.getters.appUuid,
        sequenceId: this.$route.params.sequenceId,
        pagination: this.pagination
      })
    },
    async getSequenceService () {
      await this.$store.dispatch('getSequence',
        {
          appId: this.$store.getters.appUuid,
          sequenceId: this.$route.params.sequenceId
        }
      )
    },
    async getSequenceStepsService () {
      await this.$store.dispatch('getSequenceSteps',
        {
          appId: this.$store.getters.appUuid,
          sequenceId: this.$route.params.sequenceId
        }
      )
    },
    goToSequence () {
      this.$router.push(
        {
          name: 'Sequence Leads',
          params: {
            appid: this.$store.getters.appUuid,
            sequenceId: this.$route.params.sequenceId
          }
        }
      )
    }
  }
}
</script>

<style scoped>
  .card-chart {
    border-radius: 10px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
    padding: 10px;
  }
</style>
